/**
 * Tabs for the first level of the mobile drawer menu (custom-drawer-tabs snippet).
 * Follows the WAI-ARIA tabs pattern: click or Arrow Left/Right/Home/End to switch tabs.
 */
class CustomDrawerTabs extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @returns {HTMLElement[]} */
  get tabs() {
    return [...this.querySelectorAll('[role="tab"]')].filter((tab) => tab instanceof HTMLElement);
  }

  /**
   * Shows the panel of the given tab and hides the others.
   * @param {HTMLElement} selectedTab
   * @param {boolean} [focus]
   */
  select(selectedTab, focus = false) {
    for (const tab of this.tabs) {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;

      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (panel instanceof HTMLElement) panel.hidden = !isSelected;
    }

    if (focus) selectedTab.focus();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (tab instanceof HTMLElement && this.contains(tab)) this.select(tab);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.getAttribute('role') !== 'tab') return;

    const { tabs } = this;
    const index = tabs.indexOf(event.target);
    if (index === -1) return;

    /** @type {HTMLElement | undefined} */
    let nextTab;
    if (event.key === 'ArrowRight') nextTab = tabs[(index + 1) % tabs.length];
    else if (event.key === 'ArrowLeft') nextTab = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (event.key === 'Home') nextTab = tabs[0];
    else if (event.key === 'End') nextTab = tabs.at(-1);

    if (!nextTab) return;

    event.preventDefault();
    this.select(nextTab, true);
  };
}

if (!customElements.get('custom-drawer-tabs')) {
  customElements.define('custom-drawer-tabs', CustomDrawerTabs);
}
